import {
  Home,
  BarChart3,
  CalendarCheck,
  Sparkles,
  LayoutGrid,
  Library,
  Settings,
  Plug,
  HeartPulse,
  type LucideIcon,
} from "lucide-react";
import { MODULE_REGISTRY } from "@/config/modules";

/**
 * Canonical app navigation destinations — the SINGLE SOURCE OF TRUTH consumed
 * by both the BottomNav (bottom tabs) and the Mirror hub drawer (the hidden
 * nav that descends over the Espelho). Previously this list lived inline in
 * BottomNav.tsx; centralizing it keeps the two surfaces in sync (add a
 * destination once → both pick it up via their derived slices below).
 *
 * `labelKey` is an i18n key (resolved with `t(labelKey)` at render), never a
 * literal string, so PT-BR / EN / NL stay consistent.
 */
export interface NavItem {
  /** Stable id used to slice the list per surface. */
  id: string;
  /** Route the item navigates to (must already exist in App.tsx routes). */
  path: string;
  /** lucide icon component. */
  icon: LucideIcon;
  /** i18n key for the label (e.g. "nav.home", "settings.title"). */
  labelKey: string;
  /** BottomNav-only: the raised center treatment (the Mirror tab). */
  center?: boolean;
}

export const NAV_ITEMS: NavItem[] = [
  { id: "home", path: "/home", icon: Home, labelKey: "nav.home" },
  { id: "checkin", path: "/check-in", icon: CalendarCheck, labelKey: "nav.checkin" },
  { id: "mirror", path: "/mirror", icon: Sparkles, labelKey: "nav.mirror", center: true },
  { id: "modules", path: "/modules", icon: LayoutGrid, labelKey: "nav.modules" },
  { id: "progress", path: "/progress", icon: BarChart3, labelKey: "nav.progress" },
  // Biblioteca (F1 PR-D) lives only in the drawer too — decisão travada:
  // Biblioteca = drawer do Mirror + rota /library (Progress entry fica v1.1).
  { id: "library", path: "/library", icon: Library, labelKey: "library.title" },
  // Integrações no drawer do Espelho — a segunda porta da entrega de 29/07.
  //
  // Configurações é onde vai quem JÁ SABE que a funcionalidade existe. O
  // hambúrguer do Espelho é onde a pessoa já está. Enterrar integrações só em
  // Configurações é o que produziu o número que motivou tudo isto: 7 conexões
  // de Google Calendar ativas e ZERO eventos vindos de integração em 7 dias,
  // contra 108 vindos do Espelho. Não era funil quebrado — era que ninguém
  // sabia que existia.
  //
  // Chave própria (nav.integrations) e não settings.integrations.title: aquela
  // é "INTEGRAÇÕES" em caixa alta, feita para cabeçalho de seção, e ficaria
  // gritando ao lado de "Configurações" e "Biblioteca" no menu.
  // A rota /settings/connections já existe em App.tsx.
  { id: "integrations", path: "/settings/connections", icon: Plug, labelKey: "nav.integrations" },
  // 🔴 A INVERSÃO. A tela de métricas de saúde só era alcançável POR DENTRO de
  // Integrações: a métrica morava dentro do encanamento que a produz. Agora
  // ela tem porta própria aqui, e o caminho para a integração é um botão DELA
  // para lá — não o contrário. Mesmo argumento que trouxe "integrations" para
  // este menu: Configurações é onde vai quem JÁ SABE que a coisa existe; o
  // hambúrguer do Espelho é onde a pessoa já está.
  // A rota /health/metrics já existe em App.tsx.
  { id: "health", path: "/health/metrics", icon: HeartPulse, labelKey: "nav.health" },
  // Settings lives only in the drawer (it is NOT a bottom tab — see the slice
  // below). Reuses the existing settings.title key in all 3 locales.
  { id: "settings", path: "/settings", icon: Settings, labelKey: "settings.title" },
];

/**
 * Ids que existem só no drawer do Mirror — nunca viram bottom tab.
 * "integrations" PRECISA estar aqui: sem isso viraria uma 6ª aba na
 * BottomNav, que é derivada por exclusão desta lista.
 */
const DRAWER_ONLY_IDS = new Set(["settings", "library", "integrations", "health"]);

/**
 * BottomNav = the 5 historical tabs, same order, ZERO visual change. Settings
 * and Library are excluded (they were never bottom tabs).
 */
export const BOTTOM_NAV_ITEMS: NavItem[] = NAV_ITEMS.filter((i) => !DRAWER_ONLY_IDS.has(i.id));

/** Resolve a visible tab for nested routes without matching unrelated prefixes. */
export function bottomNavPathFor(pathname: string): string | undefined {
  const matches = (path: string) => pathname === path || pathname.startsWith(`${path}/`);
  const tab = BOTTOM_NAV_ITEMS.find((item) => matches(item.path));
  if (tab) return tab.path;
  if (["/score-history", "/vyv-moments", "/identity-card"].some(matches)) return "/progress";
  if (MODULE_REGISTRY.some((item) => matches(item.route)) || matches("/brain")) return "/modules";
  return undefined;
}

/**
 * Mirror hub drawer = the hidden nav. Omits the Mirror itself (you are already
 * on it — the Espelho is the hub you always return to), so the list reads:
 * Meu Dia (/home) · Check-in · Módulos · Progresso · Settings.
 */
export const MIRROR_DRAWER_ITEMS: NavItem[] = NAV_ITEMS.filter((i) => i.id !== "mirror");
